import { isSupportedCountry } from 'libphonenumber-js';
import validator from 'validator';
import { contactDetailKinds } from 'v5/core/contact-details/contactDetailKinds';
import {
  contactDetailColumnByType,
  type ContactDetailType,
  type FieldSpecOf
} from 'v5/core/entity-schema/entitySchema.types';
import {
  type ContactDetailItem,
  type FieldCodec,
  type ValidationResult
} from 'v5/core/entity-values/values.types';
import { rowIdUtils } from 'v5/core/form-schema/formRows';
import { validateField, validation } from './validation';

/** Structural view of a `contact_details` row; the four relations share it. */
type DetailRow = {
  id: number;
  value_1?: string;
  value_2?: string;
  value_3?: string | null;
};

function isEmpty(value: ContactDetailItem[]): boolean {
  return value.length === 0;
}

function isValueWellFormed(detailType: ContactDetailType, value: string) {
  switch (detailType) {
    case 'EMAIL':
      return Boolean(validator.isEmail(value));
    case 'WEBSITE':
      return Boolean(validator.isURL(value));
    case 'PHONE':
    case 'SOCIAL':
      return true;
  }
}

/**
 * One contact-details relation (`emails` / `phone_numbers` / `socials` /
 * `websites`); `value_1` is the value, `value_2` the type. The backend
 * orders by id, so decode does too.
 */
export const contactDetailsCodec: FieldCodec<'contact-details'> = {
  decode(entity, spec) {
    const rows: DetailRow[] =
      entity[contactDetailColumnByType[spec.detailType]] ?? [];
    return [...rows]
      .sort((a, b) => a.id - b.id)
      .map(row => ({
        rowId: rowIdUtils.persisted(row.id),
        value: row.value_1 ?? '',
        type: row.value_2 ?? '',
        ...(row.value_3 && isSupportedCountry(row.value_3)
          ? { country: row.value_3 }
          : {})
      }));
  },
  encode(value, spec) {
    return {
      type: 'contact-details',
      detailType: spec.detailType,
      items: value.map(item => ({
        id: rowIdUtils.persistedId(item.rowId),
        // Validated trimmed, and a recipient or a link: the spaces of a copy go.
        value:
          spec.detailType === 'EMAIL' || spec.detailType === 'WEBSITE'
            ? item.value.trim()
            : item.value,
        type: item.type,
        ...(item.country ? { country: item.country } : {})
      }))
    };
  },
  empty() {
    return [];
  },
  isEmpty,
  validate(value, spec) {
    return validateField(value, spec, isEmpty, (value, spec) => {
      for (const [index, item] of value.entries()) {
        if (item.type === '') {
          return validation.invalid({
            code: 'part-required',
            part: 'type',
            at: `${index}.type`
          });
        }
        if (item.value === '') {
          return validation.invalid({
            code: 'part-required',
            part: 'value',
            at: `${index}.value`
          });
        }
        if (!isValueWellFormed(spec.detailType, item.value)) {
          return validation.invalid({
            code: spec.detailType === 'EMAIL' ? 'invalid-email' : 'invalid-url',
            at: `${index}.value`
          });
        }
      }
      return validation.valid();
    });
  }
};

/**
 * The rules of the row editor, which saves contact details as typed: a blank
 * row, a social without a platform, a malformed email or website block Save;
 * a phone is never blocked, only hinted at.
 */
export function validateContactDetailsAsTyped(
  value: ContactDetailItem[],
  spec: FieldSpecOf<'contact-details'>
): ValidationResult {
  return validateField(value, spec, isEmpty, () => {
    for (const [index, item] of value.entries()) {
      if (item.value.trim() === '') {
        return validation.invalid({
          code: 'part-required',
          part: 'value',
          at: `${index}.value`
        });
      }
      const error = contactDetailKinds[spec.detailType].error({
        value: item.value,
        type: item.type,
        country: item.country ?? null
      });
      const at = `${index}.${error?.part ?? 'value'}`;
      switch (error?.code) {
        case undefined:
          break;
        case 'platform-required':
          return validation.invalid({
            code: 'part-required',
            part: 'type',
            at
          });
        case 'not-an-email':
          return validation.invalid({ code: 'invalid-email', at });
        case 'not-a-website':
          return validation.invalid({ code: 'invalid-url', at });
      }
    }
    return validation.valid();
  });
}
